import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { setupAPI } from '../../services/api';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import { countries, Country, getCountryByCode, allCurrencies } from '../../data/countries';

const OrganizationSetupPage: React.FC = () => {
  useDocumentTitle('Setup Organisation');
  const [formData, setFormData] = useState({
    name: '',
    website: '',
    industry: '',
    size: '',
    country: '',
    currency: '',
  });
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Get suggested values from registration if available
  const state = location.state as { userCountry?: string; suggestedCurrency?: string } | null;

  useEffect(() => {
    if (state?.userCountry) {
      const country = getCountryByCode(state.userCountry);
      if (country) {
        setSelectedCountry(country);
        setFormData(prev => ({
          ...prev,
          country: state.userCountry || '',
          currency: country.currency,
        }));
      }
    }
  }, [state]);

  const industryOptions = [
    'Technology',
    'E-commerce',
    'Finance',
    'Healthcare',
    'Education',
    'Media & Entertainment',
    'Travel & Hospitality',
    'Non-profit',
    'Government',
    'Other'
  ];

  const sizeOptions = [
    '1-10 employees',
    '11-50 employees',
    '51-200 employees',
    '201-1000 employees',
    '1000+ employees'
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const countryCode = e.target.value;
    const country = getCountryByCode(countryCode);

    if (country) {
      setSelectedCountry(country);
      setFormData(prev => ({
        ...prev,
        country: countryCode,
        currency: country.currency,
      }));
    } else {
      setSelectedCountry(null);
      setFormData(prev => ({
        ...prev,
        country: '',
        currency: '',
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error('Please enter your organization name');
      return;
    }

    if (!formData.industry) {
      toast.error('Please select your industry');
      return;
    }

    if (!formData.size) {
      toast.error('Please select your organization size');
      return;
    }

    if (!formData.country) {
      toast.error('Please select your country');
      return;
    }

    if (!formData.currency) {
      toast.error('Please select your currency');
      return;
    }

    setIsLoading(true);
    try {
      const response = await setupAPI.createOrganization(formData);
      if (response.data.status === 'success') {
        toast.success('Organization created successfully!');
        navigate('/setup/asset');
      } else {
        toast.error(response.data.message || 'Failed to create organization');
      }
    } catch (error: any) {
      console.error('Organization setup error:', error);
      toast.error(error.response?.data?.message || 'Failed to create organization');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-vh-100 bg-light d-flex flex-column justify-content-center py-5">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-md-8 col-lg-6">
            <div className="text-center mb-4">
              <h2 className="h3 fw-bold text-dark">Set up your organization</h2>
              <p className="text-muted">Tell us about your organization to get started</p>
            </div>

            <div className="card shadow-sm">
              <div className="card-body p-4">
                <form onSubmit={handleSubmit}>
                  <div className="mb-3">
                    <label htmlFor="name" className="form-label">
                      Organization Name <span className="text-danger">*</span>
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="Enter your organization name"
                    />
                  </div>

                  <div className="mb-3">
                    <label htmlFor="website" className="form-label">
                      Website URL (Optional)
                    </label>
                    <input
                      id="website"
                      name="website"
                      type="url"
                      value={formData.website}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="https://example.com"
                    />
                  </div>

                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label htmlFor="industry" className="form-label">
                        Industry <span className="text-danger">*</span>
                      </label>
                      <select
                        id="industry"
                        name="industry"
                        required
                        value={formData.industry}
                        onChange={handleInputChange}
                        className="form-select"
                      >
                        <option value="">Select your industry</option>
                        {industryOptions.map((industry) => (
                          <option key={industry} value={industry}>
                            {industry}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-md-6 mb-3">
                      <label htmlFor="size" className="form-label">
                        Organization Size <span className="text-danger">*</span>
                      </label>
                      <select
                        id="size"
                        name="size"
                        required
                        value={formData.size}
                        onChange={handleInputChange}
                        className="form-select"
                      >
                        <option value="">Select organization size</option>
                        {sizeOptions.map((size) => (
                          <option key={size} value={size}>
                            {size}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label htmlFor="country" className="form-label">
                        Country <span className="text-danger">*</span>
                      </label>
                      <select
                        id="country"
                        name="country"
                        required
                        value={formData.country}
                        onChange={handleCountryChange}
                        className="form-select"
                      >
                        <option value="">Select country</option>
                        {countries.map((country) => (
                          <option key={country.code} value={country.code}>
                            {country.flag} {country.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-md-6 mb-3">
                      <label htmlFor="currency" className="form-label">
                        Currency <span className="text-danger">*</span>
                      </label>
                      <select
                        id="currency"
                        name="currency"
                        required
                        value={formData.currency}
                        onChange={handleInputChange}
                        className="form-select"
                      >
                        <option value="">Select currency</option>
                        {allCurrencies.map((curr) => (
                          <option key={curr.code} value={curr.code}>
                            {curr.code} - {curr.name}
                          </option>
                        ))}
                      </select>
                      {selectedCountry && formData.currency === selectedCountry.currency && (
                        <div className="form-text text-success">
                          <i className="bi bi-check-circle me-1"></i>
                          Auto-selected based on your country
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="d-grid mt-4">
                    <button
                      type="submit"
                      disabled={isLoading}
                      className="btn btn-primary btn-lg"
                    >
                      {isLoading ? <LoadingSpinner size="sm" /> : 'Continue'}
                    </button>
                  </div>
                </form>

                <div className="mt-4 text-center">
                  <div className="d-flex justify-content-center gap-2 mb-2">
                    <div className="rounded-circle bg-primary" style={{ width: '8px', height: '8px' }}></div>
                    <div className="rounded-circle bg-secondary opacity-25" style={{ width: '8px', height: '8px' }}></div>
                    <div className="rounded-circle bg-secondary opacity-25" style={{ width: '8px', height: '8px' }}></div>
                  </div>
                  <small className="text-muted">Step 1 of 3 - Organization Setup</small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrganizationSetupPage;
